import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEdit, faTrashAlt, faCopy
} from '@fortawesome/free-solid-svg-icons';
import { monthlySalaryService } from '../../services/api';
import { MonthlySalary } from '../../types';
import '../../styles/MonthlySalary.css';

const MonthlySalaryList: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<MonthlySalary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<MonthlySalary | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await monthlySalaryService.getAll();
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching monthly salary records:', error);
      showNotification('Error loading monthly salary records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const handleCreate = () => {
    navigate('/monthly-salary/create');
  };

  const handleEdit = (record: MonthlySalary) => {
    navigate(`/monthly-salary/${record.id}`);
  };

  const handleDeleteClick = (record: MonthlySalary) => {
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRecord) return;

    try {
      await monthlySalaryService.delete(selectedRecord.id);
      showNotification('Monthly salary record deleted successfully', 'success');
      fetchRecords();
      setShowDeleteModal(false);
      setSelectedRecord(null);
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error deleting monthly salary record', 'error');
    }
  };

  const handleDuplicate = async (record: MonthlySalary) => {
    try {
      const response = await monthlySalaryService.duplicate(record.id);
      showNotification('Monthly salary record duplicated successfully', 'success');
      fetchRecords();
      
      // Navigate to the new record
      if (response.data?.data?.id) {
        navigate(`/monthly-salary/${response.data.data.id}`);
      }
    } catch (error: any) {
      console.error('Error duplicating record:', error);
      const errorMessage = error.response?.data?.message || 'Error duplicating monthly salary record';
      showNotification(errorMessage, 'error');
    }
  };

  const formatMonth = (salaryMonth: string) => {
    if (!salaryMonth || salaryMonth.length !== 6) return salaryMonth;
    const year = salaryMonth.substring(0, 4);
    const month = salaryMonth.substring(4, 6);
    return `${year}-${month}`;
  };

  const formatNumber = (value: number | undefined | string) => {
    if (value === undefined || value === null || value === '') return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return Math.round(num).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDecimal = (value: number | undefined | string) => {
    if (value === undefined || value === null || value === '') return '0.0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateInsuranceUSD = (insuranceVND: number, exchRate: number): number => {
    if (!exchRate || exchRate === 0) return 0;
    return parseFloat((insuranceVND / exchRate).toFixed(1));
  };

  return (
    <div className="monthly-salary-list-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h2>Monthly Salary</h2>
        </div>
        <div className="header-right">
          <Button variant="primary" onClick={handleCreate}>
            <FontAwesomeIcon icon={faPlus} /> Create
          </Button>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <Alert 
          variant={notification.type === 'error' ? 'danger' : notification.type} 
          dismissible 
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          className="notification-alert"
        >
          {notification.message}
        </Alert>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Records table */}
      {!loading && (
        <div className="table-container">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-end">Exchange Rate</th>
                <th className="text-end">Total Salary</th>
                <th className="text-end">Total Insurance</th>
                <th className="text-end">Grand Total</th>
                <th className="text-center">Employees</th>
                <th>Created By</th>
                <th>Created Date</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No monthly salary records found. Click "Create" to add one.
                  </td>
                </tr>
              ) : (
                records.map(record => {
                  const insuranceUSD = calculateInsuranceUSD(record.total_insurance || 0, record.exch_rate || 0);
                  // Ensure values are numbers for calculation
                  const salaryVND = parseFloat(String(record.total_vnd_amount || 0));
                  const insuranceVND = parseFloat(String(record.total_insurance || 0));
                  const salaryUSD = parseFloat(String(record.total_usd_amount || 0));
                  const grandTotalVND = salaryVND + insuranceVND;
                  const grandTotalUSD = salaryUSD + insuranceUSD;
                  return (
                    <tr key={record.id}>
                      <td>{formatMonth(record.salary_month)}</td>
                      <td className="text-end">{formatNumber(record.exch_rate)}</td>
                      <td className="text-end">
                        <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: '1.3' }}>
                          {formatNumber(record.total_vnd_amount || 0)} ₫
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.3' }}>
                          ${formatDecimal(record.total_usd_amount || 0)}
                        </div>
                      </td>
                      <td className="text-end">
                        <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: '1.3' }}>
                          {formatNumber(record.total_insurance || 0)} ₫
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.3' }}>
                          ${formatDecimal(insuranceUSD)}
                        </div>
                      </td>
                      <td className="text-end">
                        <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: '1.3', color: '#10b981' }}>
                          {formatNumber(grandTotalVND)} ₫
                        </div>
                        <div style={{ fontSize: '11px', color: '#059669', lineHeight: '1.3' }}>
                          ${formatDecimal(grandTotalUSD)}
                        </div>
                      </td>
                      <td className="text-center">{record.employee_count || 0}</td>
                      <td>{record.created_by_name || '-'}</td>
                      <td>{formatDate(record.created_date)}</td>
                      <td className="text-center">
                        <div className="action-buttons">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => handleEdit(record)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            onClick={() => handleDuplicate(record)}
                            title="Duplicate"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            onClick={() => handleDeleteClick(record)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the monthly salary record for{' '}
          <strong>{selectedRecord && formatMonth(selectedRecord.salary_month)}</strong>?
          <br />
          <br />
          This will also delete all employee details for this month.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonthlySalaryList;
